import { FullHeightStack } from '@artic/ui/full-height-layout';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useQueryStates } from 'nuqs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { deriveLensAvailability } from '../../../features/cost-to-produce/lens';
import { costToProduceProjectsQueryOptions } from '../../../features/cost-to-produce/queries';
import {
  costToProduceSearchParsers,
  type CostToProduceWindow,
} from '../../../features/cost-to-produce/search';
import { CostToProduceShell } from '../../../features/cost-to-produce/section-shell';
import { useCostToProduceEnabled } from '../../../features/cost-to-produce/use-cost-to-produce-enabled';
import { useScopedFactoryParam } from '../../../features/cost-to-produce/use-scoped-factory';
import { usePartnerId } from '../../../features/inbox/use-partner-name';
import { todayIso } from '../../../features/shift-patterns/dates';

export const Route = createFileRoute('/operating-costs/cost-to-produce')({
  staticData: { breadcrumb: 'Cost to Produce', layout: 'full-height' },
  component: CostToProducePage,
});

/**
 * ENG-921 Cost to Produce.
 *
 * The flags are checked ONCE here rather than on each tab, so switching tabs
 * cannot flash a permission message. The page needs both `operating_costs` (its
 * endpoints' guard) and `cost_to_produce` (its own flag, which the sidebars gate
 * on). The shell and every tab sit inside the same full-height stack so the
 * grids get the viewport.
 */
function CostToProducePage() {
  const { t } = useTranslation('costToProduce');
  const enabled = useCostToProduceEnabled();
  const partnerId = usePartnerId();
  const [search] = useQueryStates(costToProduceSearchParsers);
  const today = todayIso();

  const scopedFactory = useScopedFactoryParam(search.factory);
  const params = useMemo(
    () => ({
      factory: scopedFactory,
      window: search.window as CostToProduceWindow,
      start: search.start,
    }),
    [scopedFactory, search.window, search.start],
  );
  // The shell needs to know which lenses the data can support, which only the
  // Project payload knows. It is the same query the Project tab reads, so this
  // costs one cache hit, not one request.
  const { data } = useQuery({
    ...costToProduceProjectsQueryOptions(partnerId, params),
    enabled: !!partnerId && enabled,
  });

  const lensAvailability = useMemo(
    () => deriveLensAvailability(data?.sources ?? []),
    [data?.sources],
  );
  const overheadsAvailable = (data?.sources ?? []).some(
    (source) => source.sources['overheads_baseline']?.state !== 'not_set',
  );

  if (!enabled) return <div className="py-20 text-center">{t('errors.notEnabled')}</div>;

  return (
    <FullHeightStack>
      <CostToProduceShell
        partnerId={partnerId}
        lensAvailability={lensAvailability}
        overheadsAvailable={overheadsAvailable}
        today={today}
      />
      <Outlet />
    </FullHeightStack>
  );
}
